import { useStore } from 'hooks/useStore';
import {
  DrugReferenceByConceptCode,
  DrugReferenceByConceptCodePayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceByConceptCodeInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { debounce } from 'lodash';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  concept_code: string;
  proprietary_name: string;
  strength: number | null;
  dosage_form: string;
  coated: number | null;
  product_color?: string;
}

export const useDrugReferenceByConceptCodeTable = (pageSize: number = 100) => {
  const { drugReferenceByConceptCodeStore, coatingTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<DrugReferenceByConceptCode | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  const dosageFormOptions = [
    {
      label: 'Capsule',
      value: 'Capsule'
    },
    {
      label: 'Tablet',
      value: 'Tablet'
    },
    {
      label: 'Other',
      value: 'Any'
    }
  ];

  // Load initial data when component mounts
  useEffect(() => {
    // Only fetch initial data if no search query
    if (!searchQuery) {
      drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
        currentPage,
        pageSize,
        undefined
      );
    }

    // Fetch coating types for dropdown in the add/edit modal
    if (coatingTypeStore.coatingTypes.length === 0) {
      coatingTypeStore.fetchCoatingTypes();
    }
  }, [currentPage, pageSize]); // Remove searchQuery from dependencies

  // Set up polling for data updates
  useInterval(() => {
    try {
      drugReferenceByConceptCodeStore.pollDrugReferenceByConceptCodes(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
    } catch (error) {
      // Don't break the polling cycle on error
      if(environment === 'development'){
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Drug Reference by Concept Code data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
        newPage,
        pageSize,
        searchQuery || undefined
      );
    }
  };

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    setSearchQuery(query);
    drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
      1,
      pageSize,
      query || undefined
    );
    setCurrentPage(1); // Reset to first page on new search
  }, 500);

  const handleSearchChange = (query: string) => {
    debouncedSearch(query);
  };

  // Prepare form data for editing
  const prepareFormData = (row: DrugReferenceByConceptCode): FormData => {
    return {
      concept_code: row.concept_code || '',
      proprietary_name: row.proprietary_name || '',
      strength: row.strength || null,
      dosage_form: row.dosage_form || '',
      coated: row.coated?.id || null,
      product_color: row.product_color || ''
    };
  };

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row - only update the editable fields
      const updatePayload: DrugReferenceByConceptCodePayload = {
        concept_code: selectedRow.concept_code || '',
        proprietary_name: selectedRow.proprietary_name || '',
        strength: selectedRow.strength || null,
        dosage_form: formData.dosage_form,
        coated: formData.coated,
        product_color: formData.product_color
      };

      const success = await drugReferenceByConceptCodeStore.updateDrugReferenceByConceptCode(
        selectedRow.id,
        updatePayload
      );
      if (!success) {
        throw new Error('Failed to update drug reference by concept code');
      }

      // Close modal immediately after successful edit
      setShowAddModal(false);
    } else {
      // Add new row - all fields are included
      const createPayload: DrugReferenceByConceptCodePayload = {
        concept_code: formData.concept_code,
        proprietary_name: formData.proprietary_name,
        strength: formData.strength,
        dosage_form: formData.dosage_form,
        coated: formData.coated,
        product_color: formData.product_color,
        flag: true
      };

      const success =
        await drugReferenceByConceptCodeStore.createDrugReferenceByConceptCode(createPayload);
      if (!success) {
        throw new Error('Failed to create drug reference by concept code');
      }

      // Close modal immediately after successful creation
      setShowAddModal(false);
    }

    setSelectedRow(null);
    // Refresh data while maintaining current page
    await drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleDeleteRow = async (): Promise<void> => {
    if (selectedRow) {
      const success = await drugReferenceByConceptCodeStore.deleteDrugReferenceByConceptCode(
        selectedRow.id
      );
      if (!success) {
        throw new Error('Failed to delete drug reference by concept code');
      }
      setSelectedRow(null);
      // Refresh data while maintaining current page
      await drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
    }
  };

  const refreshData = async () => {
    await drugReferenceByConceptCodeStore.fetchDrugReferenceByConceptCodes(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleExportToExcel = () => {
    const filteredData = drugReferenceByConceptCodeStore.drugReferenceByConceptCodes.filter(
      item => {
        if (!searchQuery.trim()) {
          return true;
        }
        const searchLower = searchQuery.toLowerCase();
        return (
          item.concept_code?.toLowerCase().includes(searchLower) ||
          item.proprietary_name?.toLowerCase().includes(searchLower) ||
          item.dosage_form?.toLowerCase().includes(searchLower) ||
          item.strength?.toString().includes(searchQuery)
        );
      }
    );

    const exportData = filteredData.map(item => ({
      'Concept Code': item.concept_code,
      'Proprietary Name': item.proprietary_name,
      Strength: item.strength,
      'Dosage Form': item.dosage_form,
      Coated: item.coated ? item.coated.description : 'N/A',
      "Product color": item.product_color
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Drug Reference by Concept Code');
    XLSX.writeFile(workbook, 'drug_reference_by_concept_code_export.xlsx');
  };

  return {
    drugReferenceData: drugReferenceByConceptCodeStore.drugReferenceByConceptCodes,
    isLoading: drugReferenceByConceptCodeStore.isLoading,
    pagination: drugReferenceByConceptCodeStore.pagination,
    coatingOptions: [
      { label: 'N/A', value: null },
      ...coatingTypeStore.coatingTypes.map(coating => ({
        label: coating.description || '',
        value: coating.id
      }))
    ],
    isLoadingCoatingTypes: coatingTypeStore.isLoading,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    handleSearchChange,
    prepareFormData,
    onPageChange,
    refreshData,
    dosageFormOptions,
    handleExportToExcel
  };
};
